import { PersonAdd } from '@mui/icons-material';
import {
  Box,
  DialogActions,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import { getCompanyUsers, getOtherCompanyUsers, updateUser } from 'lib/api';
import CustomButton from 'lib/designSystem/Button';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import Title from 'lib/designSystem/Title';
import { errorHandler } from 'lib/functions/errors';
import {
  rutChanger,
  showAlert,
  validaRut,
  validateChileanPhone,
} from 'lib/functions/utils';
import { User } from 'lib/types/models';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

interface AssignSignerProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  fetchSigners: () => Promise<void>;
  businessIdentifier?: string;
}

interface InputErrors {
  identifier: string;
  email: string;
  phone: string;
}

const validateField = (field: keyof InputErrors, value: string): string => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (field === 'email') {
    return emailRegex.test(value) ? '' : 'Please enter a valid email';
  }
  if (field === 'phone') {
    return validateChileanPhone(value)
      ? ''
      : 'Phone must be a valid Chilean number';
  }
  if (field === 'identifier') {
    return validaRut(value) ? '' : 'RUT is invalid. Use 12345678-K format';
  }
  return '';
};

const AssignSigner = ({
  open,
  setOpen,
  fetchSigners,
  businessIdentifier,
}: AssignSignerProps) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<number | null>(null);
  const [identifier, setIdentifier] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [errors, setErrors] = useState<InputErrors>({
    email: '',
    phone: '',
    identifier: '',
  });

  const nonSigners = useMemo(() => {
    return users.filter((user) => !user.isSigner);
  }, [users]);

  const fetchCompanyUsers = async () => {
    try {
      setLoading(true);
      if (businessIdentifier) {
        const users = await getOtherCompanyUsers(dispatch, businessIdentifier);
        setUsers(users);
      } else {
        const users = await getCompanyUsers(dispatch);
        setUsers(users);
      }
    } catch (error) {
      errorHandler(dispatch, error);
    } finally {
      setLoading(false);
    }
  };

  const handleAssignSigner = async () => {
    try {
      setErrors({ email: '', phone: '', identifier: '' });

      const nextErrors: InputErrors = {
        email: validateField('email', email),
        phone: validateField('phone', phone),
        identifier: validateField('identifier', identifier),
      };

      const hasErrors = Object.values(nextErrors).some(Boolean);
      if (hasErrors) {
        setErrors(nextErrors);
        return;
      }
      if (!selectedUser) {
        return showAlert(dispatch, 'error', 'Debes seleccionar un firmante');
      }
      setLoading(true);
      const payload = {
        id: selectedUser,
        isSigner: true,
        identifier,
        email,
        phone,
      };
      await updateUser(dispatch, payload);
      await fetchCompanyUsers();
      await fetchSigners();
      showAlert(dispatch, 'success', 'Firmante asignado correctamente');
      setOpen(false);
    } catch (error) {
      errorHandler(dispatch, error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const { value } = e.target;
    const user = users.find((user) => user.id === Number(value));
    setSelectedUser(user?.id || null);
    setIdentifier(user?.identifier || '');
    setEmail(user?.email || '');
    setPhone(user?.phone || '');
  };

  const handleBlur = (field: keyof InputErrors, value: string) => {
    const message = validateField(field, value);
    setErrors((prev) => ({ ...prev, [field]: message }));
  };

  useEffect(() => {
    void fetchCompanyUsers();
  }, [dispatch]);

  if (loading) return <CenteredCircularProgress type="dialog" />;

  return (
    <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
      <Box mt={2} mx={2.5}>
        <Title
          title="Asignar firmante"
          icon={<PersonAdd color="primary" />}
          color="primary"
        />
      </Box>
      <Box
        mt={2}
        mx={2.5}
        sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
      >
        <FormControl fullWidth>
          <InputLabel id="signer-label">Firmante</InputLabel>
          <Select
            labelId="signer-label"
            id="signer-select"
            label="Firmantes"
            value={selectedUser?.toString() || ''}
            onChange={handleSelectChange}
          >
            {nonSigners.map((user) => (
              <MenuItem key={user.id} value={user.id}>
                {user.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label="Rut"
          placeholder="12345678-K"
          value={identifier}
          onChange={(e) => {
            rutChanger(e.target.value, (val) => {
              setIdentifier(val);
              setErrors((prev) => ({
                ...prev,
                identifier: validateField('identifier', val),
              }));
            });
          }}
          onBlur={(e) => handleBlur('identifier', e.target.value)}
          fullWidth
          error={Boolean(errors.identifier)}
          helperText={errors.identifier}
        />
        <TextField
          label="Email"
          type="email"
          value={email}
          onChange={(e) => {
            const val = e.target.value;
            setEmail(val);
            setErrors((prev) => ({
              ...prev,
              email: validateField('email', val),
            }));
          }}
          onBlur={(e) => handleBlur('email', e.target.value)}
          fullWidth
          error={Boolean(errors.email)}
          helperText={errors.email}
        />
        <TextField
          label="Phone"
          placeholder="56912345678"
          value={phone}
          onChange={(e) => {
            const val = e.target.value.replace(/\s/g, '');
            setPhone(val);
            setErrors((prev) => ({
              ...prev,
              phone: validateField('phone', val),
            }));
          }}
          onBlur={(e) => handleBlur('phone', e.target.value)}
          fullWidth
          error={Boolean(errors.phone)}
          helperText={errors.phone}
        />
      </Box>
      <DialogActions>
        <CustomButton
          color="primary"
          loading={loading}
          onClick={handleAssignSigner}
        >
          Asignar
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default AssignSigner;
